import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Params } from '@angular/router';
import { getExhibitionSettings, getLanguage, getOperatorsSettings, getUserLanguage } from '@app/app.reducer';
import { InitializationService } from '@core/data-access/models/service.model';
import { Store } from '@ngrx/store';
import { DEFAULT_LANGUAGE, Language, TranslateService } from '@ngx-translate/core';
import { HelperService } from '@store/helpers/helper.service';
import { UserService } from '@store/user/user.service';
import { defaultTo, find, first, includes, isEmpty, toLower } from 'lodash';
import { combineLatest, filter } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class LanguageManager implements InitializationService {
  readonly #destroyRef = inject(DestroyRef);
  readonly #store = inject(Store);
  readonly #route = inject(ActivatedRoute);
  readonly #defaultLanguage = inject(DEFAULT_LANGUAGE);
  readonly #translateService = inject(TranslateService);
  readonly #userService = inject(UserService);
  readonly #helperService = inject(HelperService);

  init() {
    this.initSetLanguageSubscription();
  }

  private initSetLanguageSubscription() {
    return combineLatest({
      userLanguage: this.#store.select(getUserLanguage),
      language: this.#store.select(getLanguage),
      operatorSettings: this.#store.select(getOperatorsSettings),
      exhibitionSettings: this.#store.select(getExhibitionSettings),
      queryParams: this.#route.queryParams
    })
      .pipe(
        filter(
          ({ operatorSettings, exhibitionSettings }) => !isEmpty(operatorSettings) || !isEmpty(exhibitionSettings)
        ),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(({ userLanguage, language, operatorSettings, exhibitionSettings, queryParams }) => {
        const { languages: operatorSettingsLanguages, defaultLanguage: operatorSettingsDefaultLanguage } =
          operatorSettings;

        const supportedLanguages = this.resolveSupportedLanguages(
          operatorSettingsLanguages,
          exhibitionSettings?.languages,
          operatorSettingsDefaultLanguage
        );
        const activeLanguage = this.resolveActiveLanguage(
          queryParams,
          supportedLanguages,
          userLanguage,
          language,
          operatorSettingsDefaultLanguage
        );

        this.#helperService.setLanguage(activeLanguage);
        this.#helperService.setSupportedLanguages(supportedLanguages);
      });
  }

  private resolveSupportedLanguages(
    operatorSettingsLanguages: Language[],
    exhibitionSettingsLanguages: Language[],
    operatorSettingsDefaultLanguage: Language
  ) {
    if (!isEmpty(exhibitionSettingsLanguages)) {
      return exhibitionSettingsLanguages;
    }

    if (!isEmpty(operatorSettingsLanguages)) {
      return operatorSettingsLanguages;
    }

    return [operatorSettingsDefaultLanguage];
  }

  private resolveActiveLanguage(
    params: Params,
    supportedLanguages: Language[],
    userLanguage: Language,
    language: Language,
    operatorSettingsDefaultLanguage: Language
  ) {
    const paramLanguage = defaultTo(toLower(params?.lang), null);
    const isSelfRegistration = this.#helperService.isSelfRegistration();
    const supportsUserLanguage = includes(supportedLanguages, userLanguage);
    const browserLanguage = this.#translateService.getBrowserLang();

    const activeLanguageCheck: Language[] = [
      paramLanguage,
      isSelfRegistration && supportsUserLanguage ? userLanguage : null,
      isSelfRegistration ? 'de' : null,
      supportsUserLanguage ? userLanguage : null,
      language,
      browserLanguage,
      operatorSettingsDefaultLanguage,
      this.#defaultLanguage
    ];

    const activeLanguage =
      find(activeLanguageCheck, (language) => includes(supportedLanguages, language)) || first(supportedLanguages);

    if (activeLanguage === paramLanguage) {
      this.#userService.changePreferredLanguage(activeLanguage);
    }

    return activeLanguage;
  }
}
